import { createSignal, Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { ImageWordpress } from '../../types/shared';
import { VideoData } from '../videos/videos-types';
import { TabHandler } from '../../tools/tab-handler';
import { Modal } from '../../ui-components/modal/modal';
import { FaqContentModal, FaqContentModalHeader } from './faq-content-modal';

type LinkType = {
    url?: string;
    title?: string;
}

export type FaqItemType = {
    category: string;
    question: string;
    blocks: any[];
    mediaType?: 'image' | 'video';
    image: ImageWordpress;
    video?: VideoData;
    link1?: LinkType;
    link2?: LinkType;
};

type FaqItemProps = {
    faqItem: FaqItemType;
};

/* The card follows the DS `ds-card-article` minus its figure: 8px radius, the DS
   content inset and body-xs meta / heading-md title pair, and a bg shift rather
   than a shadow on hover.

   Two departures, both because this block isn't in the setting the DS card assumes:
   - Border. `ds-card-article` sets `border: 0`, since DS pages place cards on a
     tinted `ds-block`. This block has no background of its own, so on a white page
     a borderless white card is invisible until hover. The 1px `border-muted` is the
     DS `ds-surface` treatment for a standalone card, and it disappears into a
     tinted section.
   - Padding-bottom is ds-space(6), not the card's ds-space(9). The trailing element
     is a small right-aligned link rather than a wrapping title, so the DS value
     left a visible gap beneath it. */
export const FaqItem = (props: FaqItemProps) => {
    const { localize } = useContext(AppContext);

    const [ modalVisible, setModalVisible ] = createSignal(false);

    // Accessibility/keyboard-controls related
    const [ firstTabbableElement, setFirstTabbableElement ] = createSignal<HTMLElement | undefined>();
    const [ lastTabbableElement, setLastTabbableElement ] = createSignal<HTMLElement | undefined>();

    return (
        <li>
            <button
                type="button"
                aria-haspopup="dialog"
                class={[
                    'group block w-full max-w-[58rem] cursor-pointer overflow-clip rounded-cvi-md text-left',
                    'border border-cvi-border-muted bg-cvi-bg-block-default',
                    'transition-colors duration-200 ease-cvi-standard',
                    'hover:bg-cvi-bg-neutral-subtle',
                ].join(' ')}
                onClick={() => setModalVisible(true)}
            >
                <span class="flex flex-col gap-cvi-2 px-cvi-6 pt-cvi-8 pb-cvi-6">
                    <span class="body-xs font-cvi-medium text-cvi-fg-default">
                        {props.faqItem.category}
                    </span>

                    <span class="heading-md font-cvi-medium m-0 text-cvi-fg-default">
                        {props.faqItem.question}
                    </span>

                    {/* Own line at the card's bottom-right. `self-end` rather than
                        `text-right` so it stays flush in RTL too. */}
                    <span class="body-sm font-cvi-medium mt-cvi-4 self-end whitespace-nowrap text-cvi-fg-link group-hover:underline">
                        {localize('read-more', 'Read more')}
                    </span>
                </span>
            </button>

            <Show when={modalVisible()}>
                <TabHandler refs={{
                    firstTabbableElement,
                    lastTabbableElement,
                }}>
                    <Modal
                        visible={true}
                        onClose={() => setModalVisible(false)}
                        size='full'
                        darkenBackground={true}
                        closeButtonRef={(el: HTMLElement) => setFirstTabbableElement(el)}
                        header={<FaqContentModalHeader faqItem={props.faqItem} />}
                    >
                        <FaqContentModal
                            faqItem={props.faqItem}
                            onClose={() => setModalVisible(false)}
                            setLastTabbableElement={(el: HTMLElement) => setLastTabbableElement(el)}
                        />
                    </Modal>
                </TabHandler>
            </Show>
        </li>
    );
};
